/**
 * Live business rules (ADR 0039).
 *
 * Until now the app ran entirely on the JS defaults in config/business-rules.js
 * ("offline defaults for now") — which made the admin-editable rules table
 * write-only in practice: an admin could change a row and no client would ever
 * notice. This module closes the loop, offline-first:
 *
 *   boot:    resolve rules from the LAST CACHED rows (IndexedDB meta) — no
 *            network on the critical path, works cold-offline, and a device
 *            that has synced once keeps its admin-tuned rules forever.
 *   background: after boot (and again on sign-in), pull the global rows and
 *            refresh the cache. Fresh rules take effect on the NEXT load —
 *            swapping the rule set under a live sketch mid-gesture is a
 *            correctness hazard for nothing (the admin panel itself reloads
 *            immediately after saving, so the editor always sees their edit).
 *
 * Scope note: only scope='global' rows are pulled for now. Project/room scoped
 * overrides (the table supports them) get a design pass when the owner first
 * needs one — resolveBusinessRules takes a flat map, so the merge point is
 * here, not in every caller.
 */

import { fetchAllKeyset } from './sync-client.js';

const META_KEY = 'businessRulesRows';

/** rule_key -> parsed rule_value map from the cache, or {} when never synced. */
export async function cachedRuleRows(getMeta) {
  try {
    const rows = await getMeta(META_KEY);
    return rows && typeof rows === 'object' ? rows : {};
  } catch {
    return {}; // a broken cache must never block boot — defaults still work
  }
}

/**
 * Pulls the active global rules and refreshes the cache. Returns the fresh
 * map, or null when unavailable (signed out, offline, RLS denied) — callers
 * treat null as "keep whatever we had".
 */
export async function pullBusinessRules(supabase, setMeta) {
  // PAGED like every other bulk read. This was the LAST unbounded one — the
  // residual sibling the pagination work recorded and deliberately left, on the
  // grounds that global rules number in the dozens. That reasoning is fine
  // until it is not: the table already supports project- and room-scoped rows
  // (see the scope note above), so the day someone needs one is the day this
  // read starts returning hundreds, and PostgREST would truncate it in silence
  // exactly as it did the rooms.
  //
  // Sharing sync-client's ONE paging implementation rather than growing a
  // second: a fix that lives in one file and not the next is how this class
  // survived to be found twice.
  const res = await fetchAllKeyset(() => supabase
    .schema('sketch')
    .from('business_rules')
    .select('rule_key, rule_value')
    .eq('scope', 'global')
    .eq('is_active', true)
    .order('rule_key'), 'rule_key');
  // A failure here is why a device can silently run the compiled-in FALLBACK
  // type list while the owner sees their full Admin list elsewhere (owner,
  // 2026-08-04). Swallowing it made that indistinguishable from "no rules have
  // changed", so the reason is handed back rather than dropped.
  // platform-boundary-exception: propagates the raw Postgrest error upward
  // intact for the caller to decide, not a classification decision itself
  // — see platform/boundary-exceptions.json for the reviewed reason.
  if (res.error) {
    const err = new Error(res.error.message || 'rules pull failed');
    err.code = res.error.code;
    throw err;
  }
  const map = {};
  for (const row of res.rows ?? []) map[row.rule_key] = row.rule_value;
  await setMeta(META_KEY, map);
  return map;
}
